import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CheckCircle, XCircle, AlertCircle, TrendingUp, Globe, Target } from 'lucide-react';

const GrantScreeningDashboard = () => {
  const [selectedView, setSelectedView] = useState('overview');

  // Data based on analysis
  const yearlyData = [
    { year: 2020, submissions: 5470, promoted: 4, rate: 0.07 },
    { year: 2021, submissions: 3608, promoted: 5, rate: 0.14 },
    { year: 2022, submissions: 4367, promoted: 6, rate: 0.14 },
    { year: 2023, submissions: 5451, promoted: 11, rate: 0.20 },
    { year: 2024, submissions: 9217, promoted: 3, rate: 0.03 },
    { year: 2025, submissions: 5725, promoted: 0, rate: 0.00 }
  ];

  const dispositionData = [
    { name: 'Declined', value: 24646, color: '#ef4444' },
    { name: 'Declined by Staff', value: 5390, color: '#f87171' },
    { name: 'In Progress', value: 3343, color: '#3b82f6' },
    { name: 'Promoted', value: 29, color: '#10b981' },
    { name: 'Assigned', value: 430, color: '#8b5cf6' }
  ];

  const regionData = [
    { region: 'United States', submissions: 22215, promoted: 21 },
    { region: 'India', submissions: 3627, promoted: 2 },
    { region: 'South Africa', submissions: 3429, promoted: 6 },
    { region: 'Uganda', submissions: 483, promoted: 0 },
    { region: 'Kenya', submissions: 410, promoted: 0 },
    { region: 'Other', submissions: 5674, promoted: 0 }
  ];

  const declineReasons = [
    { reason: 'General Operating Support', count: 3504 },
    { reason: 'Technology/Equipment', count: 1010 },
    { reason: 'Capital Campaign', count: 661 },
    { reason: 'Low Amount (<$25k)', count: 1524 },
    { reason: 'Other Focus Area', count: 280 },
    { reason: 'Arts & Culture', count: 170 }
  ];

  const promotedFocusAreas = [
    { area: 'K-12 Education', count: 15, percentage: 51.7 },
    { area: 'Higher Education', count: 9, percentage: 31.0 },
    { area: 'Youth Development', count: 2, percentage: 6.9 },
    { area: 'Other', count: 3, percentage: 10.4 }
  ];

  const screeningTiers = [
    {
      tier: 1,
      name: 'Basic Eligibility',
      automated: true,
      reductionRate: 45,
      criteria: ['Geographic filter', 'Minimum funding ($25k)', 'Excluded support types']
    },
    {
      tier: 2,
      name: 'Focus Alignment',
      automated: true,
      reductionRate: 25,
      criteria: ['Focus area match', 'Organization type', 'Target population']
    },
    {
      tier: 3,
      name: 'Quality Assessment',
      automated: false,
      reductionRate: 20,
      criteria: ['Problem clarity', 'Budget rationality', 'Impact metrics']
    },
    {
      tier: 4,
      name: 'Strategic Review',
      automated: false,
      reductionRate: 9.9,
      criteria: ['Innovation', 'Scalability', 'Portfolio fit']
    }
  ];

  const MetricCard = ({ title, value, subtext, icon: Icon, trend }: { title: string; value: string; subtext: string; icon?: any; trend?: number }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        {Icon && <Icon className="w-5 h-5 text-gray-400" />}
      </div>
      <div className="flex items-baseline">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {trend && (
          <span className={`ml-2 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mt-1">{subtext}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dell Foundation Grant Screening Dashboard
          </h1>
          <p className="text-gray-600">
            Analyzing 33,838 grant proposals (2020-2025) | 0.09% promotion rate
          </p>
        </div>

        {/* Navigation */}
        <div className="flex space-x-4 mb-6">
          {['overview', 'screening', 'patterns', 'recommendations'].map((view) => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedView === view
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview View */}
        {selectedView === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                title="Total Proposals"
                value="33,838"
                subtext="Since 2020"
                icon={Target}
              />
              <MetricCard
                title="Promoted Projects"
                value="29"
                subtext="0.09% success rate"
                icon={CheckCircle}
                trend={-55}
              />
              <MetricCard
                title="Avg Review Time"
                value="87%"
                subtext="Could be automated"
                icon={TrendingUp}
              />
              <MetricCard
                title="Focus on Education"
                value="86%"
                subtext="Of promoted projects"
                icon={Globe}
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Yearly Trends */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Submission Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={yearlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="submissions"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="Submissions"
                    />
                    <Line
                      type="monotone"
                      dataKey="promoted"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Promoted"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Disposition Breakdown */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Proposal Outcomes</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dispositionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {dispositionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Screening View */}
        {selectedView === 'screening' && (
          <div className="space-y-6">
            {/* Screening Pipeline */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Proposed Screening Pipeline</h3>
              <div className="space-y-4">
                {screeningTiers.map((tier, index) => (
                  <div key={tier.tier} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      tier.automated ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                    }`}>
                      {tier.tier}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{tier.name}</h4>
                      <p className="text-sm text-gray-600">
                        {tier.automated ? '🤖 Automated' : '👤 Human Review'} |
                        Filters ~{tier.reductionRate}% of remaining
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {tier.criteria.map((criterion, idx) => (
                          <span key={idx} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                            {criterion}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {index === 0 ? '33,838' :
                         index === 1 ? '18,610' :
                         index === 2 ? '13,958' : '11,166'}
                      </p>
                      <p className="text-sm text-gray-500">proposals</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Decline Reasons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Top Automated Decline Reasons</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={declineReasons} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="reason" type="category" width={150} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Patterns View */}
        {selectedView === 'patterns' && (
          <div className="space-y-6">
            {/* Success Patterns */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Success Patterns (29 Promoted Projects)</h3>

              {/* Focus Areas */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">Focus Area Distribution</h4>
                <div className="space-y-2">
                  {promotedFocusAreas.map((area) => (
                    <div key={area.area} className="flex items-center">
                      <div className="w-32 text-sm text-gray-600">{area.area}</div>
                      <div className="flex-1 bg-gray-200 rounded-full h-6 mr-2">
                        <div
                          className="bg-green-500 h-6 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${area.percentage}%` }}
                        >
                          <span className="text-xs text-white font-medium">{area.count}</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 w-12">{area.percentage}%</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Budget Range Analysis */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">Promoted Project Budget Ranges</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Minimum</p>
                    <p className="text-2xl font-bold text-green-600">$50,000</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Maximum</p>
                    <p className="text-2xl font-bold text-green-600">$80,000,000</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Median</p>
                    <p className="text-2xl font-bold text-blue-600">$1,500,000</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Sweet Spot</p>
                    <p className="text-2xl font-bold text-blue-600">$500K-$5M</p>
                  </div>
                </div>
              </div>

              {/* Regional Success */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Regional Success Rates</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={regionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="submissions" fill="#3b82f6" name="Total Submissions" />
                    <Bar dataKey="promoted" fill="#10b981" name="Promoted" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Key Insights */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Key Success Indicators</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-medium text-gray-900 mb-2">✅ Strong Indicators</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Education focus (86% of promoted)</li>
                    <li>• Budget range $500K-$5M</li>
                    <li>• US or South Africa location</li>
                    <li>• Clear scalability plan</li>
                    <li>• Measurable impact metrics</li>
                  </ul>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-medium text-gray-900 mb-2">❌ Weak Indicators</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• General operating support</li>
                    <li>• Technology/equipment only</li>
                    <li>• Budget under $25K</li>
                    <li>• Arts & Culture focus</li>
                    <li>• No clear beneficiaries</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations View */}
        {selectedView === 'recommendations' && (
          <div className="space-y-6">
            {/* Implementation Roadmap */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Implementation Roadmap</h3>

              {/* Phase 1 */}
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Phase 1: Quick Wins (Month 1-2)
                  </div>
                </div>
                <div className="space-y-3 ml-4">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">Update OGP Form</p>
                      <p className="text-sm text-gray-600">Add validation rules to prevent ineligible submissions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">Geographic Pre-screening</p>
                      <p className="text-sm text-gray-600">Implement East/Southern Africa filter for African submissions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">Structured Decline Reasons</p>
                      <p className="text-sm text-gray-600">Implement dropdown for standardized rejection tracking</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase 2 */}
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Phase 2: AI Integration (Month 3-4)
                  </div>
                </div>
                <div className="space-y-3 ml-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">Develop Screening Agent</p>
                      <p className="text-sm text-gray-600">Train on 30K+ historical proposals and outcomes</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">Quality Scoring Algorithm</p>
                      <p className="text-sm text-gray-600">NLP analysis of problem statements and impact metrics</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase 3 */}
              <div>
                <div className="flex items-center mb-3">
                  <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Phase 3: Optimization (Month 5-6)
                  </div>
                </div>
                <div className="space-y-3 ml-4">
                  <div className="flex items-start">
                    <XCircle className="w-5 h-5 text-gray-400 mt-0.5 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">Performance Dashboard</p>
                      <p className="text-sm text-gray-600">Real-time monitoring of screening effectiveness</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <XCircle className="w-5 h-5 text-gray-400 mt-0.5 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">Feedback Loop</p>
                      <p className="text-sm text-gray-600">Continuous improvement based on PM decisions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Expected Impact */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Expected Impact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-1">70%</div>
                  <p className="text-sm text-gray-600">Reduction in PM review time</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-3xl font-bold text-green-600 mb-1">3x</div>
                  <p className="text-sm text-gray-600">Higher quality proposals reaching PMs</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600 mb-1">24hrs</div>
                  <p className="text-sm text-gray-600">Average initial response time</p>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Immediate Next Steps</h3>
              <ol className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-gray-200 text-gray-700 w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium mr-3">1</span>
                  <div>
                    <p className="font-medium text-gray-900">Validate tier thresholds with Programs team</p>
                    <p className="text-sm text-gray-600">Schedule review session with Todd, Thashlin, and Aliya</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-gray-200 text-gray-700 w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium mr-3">2</span>
                  <div>
                    <p className="font-medium text-gray-900">Run pilot on Q4 2024 data</p>
                    <p className="text-sm text-gray-600">Test automated screening on 2,000+ recent proposals</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-gray-200 text-gray-700 w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium mr-3">3</span>
                  <div>
                    <p className="font-medium text-gray-900">Update website messaging</p>
                    <p className="text-sm text-gray-600">Clarify eligibility criteria and focus areas</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrantScreeningDashboard;